import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import AkCrossIcon from '@atlaskit/icon/glyph/cross';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Inline } from '@atlaskit/primitives';
import Button from '@atlaskit/button';

import {
	ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE,
	ExperienceSuccess,
} from '@confluence/experience-tracker';
import { useOnboardingState } from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { useSampleContentExperiment } from '@confluence/experiment-sample-content';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useOnboardingTrackerStoreActions } from '@confluence/experiment-onboarding-tracker/entry-points/OnboardingTrackerStore';

import { useWindowSize } from '@confluence/dom-helpers';

import { getAdjustedTopPropertyValue } from './utils/getAdjustedTopPropertyValue';
import { TemplateBannerIllustration } from './TemplateBannerIllustration/TemplateBannerIllustration';
import {
	TemplateBannerContainer,
	IconContainer,
	StyledButton,
	TitleWrapper,
	BodyWrapper,
	LeftWrapper,
	RightWrapper,
	Background,
	templateSidebarWidth,
} from './styled';
import type { EditorBannerProps, EditorBannerContainerProps } from './OnboardingEditorBanner';
import { useOnboardingEditorActions } from './useOnboardingEditorState';

const i18n = defineMessages({
	bannerTitle: {
		id: 'onboarding-editor.land-in-editor.banner.title',
		defaultMessage: 'There’s a template for that',
	},
	bannerDescription: {
		id: 'onboarding-editor.land-in-editor.banner.description',
		defaultMessage:
			'Based on your responses, here’s a template best suited to help you get started.',
	},
	exploreCTA: {
		id: 'onboarding-editor.land-in-editor.explore-templates',
		defaultMessage: 'Explore all templates',
	},
	blankPageCTA: {
		id: 'onboarding-editor.land-in-editor.blank-page',
		defaultMessage: 'Start with a blank page',
		description: 'This message refers to starting with a Confluence page that has no content',
	},
});

export const LandInEditorBanner = (props: EditorBannerProps & EditorBannerContainerProps) => {
	const {
		isContentCreationPanelOpen,
		localUserOffline,
		setIsVisible,
		deserializedData: { isLandInEditorBannerVisible } = {},
		setIsOnboardingBannerDataLoaded,
		editorActions,
		onClickOpenContentCreationPanel,
		handleTitleChange,
	} = props;

	const { setHasLandInEditorBannerVisible } = useOnboardingEditorActions();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setIsTrackerPopupOpen, setIsMemorizedTrackerPopupMinimized } =
		useOnboardingTrackerStoreActions();
	const { setOnboardingState } = useOnboardingState();
	const sampleContentExperiment = useSampleContentExperiment();
	useEffect(() => {
		if (isLandInEditorBannerVisible) {
			void setOnboardingState([
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.IS_LAND_IN_EDITOR_BANNER_VISIBLE,
					value: 'false',
				},
			]);

			// Do not show the LandInEditorBanner for sampleContentExperiment
			if (sampleContentExperiment.isUserInTreatment) {
				return;
			}

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'landInEditorBanner',
				},
			}).fire();
			setHasLandInEditorBannerVisible(true);
			void setIsTrackerPopupOpen(false);
			void setIsMemorizedTrackerPopupMinimized(true);
		}
		// TODO: Refactor https://hello.atlassian.net/browse/CONFONBO-1674
		setTimeout(() => {
			setIsOnboardingBannerDataLoaded(true);
		}, 0);
	}, [
		setOnboardingState,
		isLandInEditorBannerVisible,
		createAnalyticsEvent,
		setIsOnboardingBannerDataLoaded,
		setHasLandInEditorBannerVisible,
		setIsTrackerPopupOpen,
		setIsMemorizedTrackerPopupMinimized,
		sampleContentExperiment,
	]);

	const editorNode: HTMLElement | null = document.querySelector('.ak-editor-content-area');

	const { width: windowWidth } = useWindowSize();
	useEffect(() => {
		// shift the editor content down for different banner breakpoints
		if (editorNode) {
			if (windowWidth <= 375) {
				editorNode.style.marginTop = '300px';
			} else if (
				windowWidth <= 1024 ||
				(isContentCreationPanelOpen && windowWidth <= 1024 + templateSidebarWidth)
			) {
				editorNode.style.marginTop = '291px';
			} else {
				editorNode.style.marginTop = '174px';
			}
		}
		return () => {
			if (editorNode) editorNode.style.marginTop = '0';
		};
	}, [editorNode, windowWidth, isContentCreationPanelOpen]);

	const handleExploreCTA = async () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'lieBannerExploreTemplates',
				source: 'landInEditorBanner',
			},
		}).fire();
		onClickOpenContentCreationPanel();
		setIsVisible(false);
	};

	const handleBlankPageCTA = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'lieBannerBlankPage',
				source: 'landInEditorBanner',
			},
		}).fire();
		onClickOpenContentCreationPanel();
		editorActions.clear();
		setIsVisible(false);
		handleTitleChange('');
	};

	const _onDismiss = () => {
		// this will start the animation, and onAnimationEnd we should hide the component
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'lieBannerDismiss',
				source: 'landInEditorBanner',
			},
		}).fire();
		setIsVisible(false);
	};

	// compiled shouldn't handle a complicated runtime CSS, hence the inlined style
	const calculatedTop = useMemo(
		() => getAdjustedTopPropertyValue(localUserOffline),
		[localUserOffline],
	);

	return (
		<>
			<TemplateBannerContainer
				style={{ top: calculatedTop }}
				isContentCreationPanelOpen={isContentCreationPanelOpen}
				data-testid="onboarding-land-in-editor-banner"
				fade={false}
			>
				<LeftWrapper>
					<TemplateBannerIllustration />
					<Background isContentCreationPanelOpen={isContentCreationPanelOpen} />
				</LeftWrapper>
				<RightWrapper isContentCreationPanelOpen={isContentCreationPanelOpen}>
					<TitleWrapper>
						<FormattedMessage {...i18n.bannerTitle} />
					</TitleWrapper>
					<BodyWrapper isContentCreationPanelOpen={isContentCreationPanelOpen}>
						<FormattedMessage {...i18n.bannerDescription} />
					</BodyWrapper>
					<Inline space="space.050">
						<StyledButton
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={{ width: 'fit-content' }}
							onClick={handleExploreCTA}
						>
							<FormattedMessage {...i18n.exploreCTA} />
						</StyledButton>
						<Button appearance="subtle" onClick={handleBlankPageCTA}>
							<FormattedMessage {...i18n.blankPageCTA} />
						</Button>
					</Inline>
				</RightWrapper>
				<IconContainer data-testid="onboarding-land-in-editor-banner-close" onClick={_onDismiss}>
					<AkCrossIcon label="dismiss" size="small" />
				</IconContainer>
			</TemplateBannerContainer>
			<ExperienceSuccess name={ONBOARDING_LAND_IN_EDITOR_BANNER_EXPERIENCE} />
		</>
	);
};
